/* ============================================================
   SENTINEL — Bundle calculator & cart drawer suggestion (variant-aware)
   Loaded by sections/sentinel-pdp-bundle.liquid on Sentinel PDPs.

   Reads the data blob emitted by the bundle section, which pre-resolves
   every variant's component set:

     data = {
       parent_handle, kit_name, free_delivery_threshold_cents,
       by_variant_id: {
         "<variantId>": {
           sku, format, discount_percent, discount_code,
           components: [ {role, handle, label, quantity, required,
                          variant_id, price_cents, available, image}, ... ]
         }, ...
       }
     }

   Responsibilities:
   1. Re-render component list when 'variant:change' event fires (from
      sentinel-pdp.js variant pill click).
   2. "Add the kit" submits the current variant's components to
      /cart/add.js then applies the SENTINEL_KIT_* code via /discount/<code>.
   3. Cart drawer suggestion: when the drawer opens with a Sentinel fence
      in cart and no kit added, inject a suggestion banner. Session-scoped
      dismissal in sessionStorage.

   Phase 1 scope: bundle JS only loads on Sentinel PDPs. Cart drawer
   suggestion works in the dominant flow (add → drawer opens). Site-wide
   loading deferred to phase 2.
   ============================================================ */

(function () {
  'use strict';

  if (window.__sentinelBundleInit) return;
  window.__sentinelBundleInit = true;

  var SESSION_DISMISSED_KEY = 'spdp_bundle_dismissed';
  var INJECTED_ID = 'sentinel-bundle-cart-suggestion';
  var FREE_DELIVERY_THRESHOLD_CENTS = 9500;

  /* ---------- DOM helpers ---------- */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.from((root || document).querySelectorAll(sel)); }

  function escapeHtml(s) {
    if (s == null) return '';
    var str = String(s);
    var map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
    return str.replace(/[&<>"']/g, function (m) { return map[m]; });
  }

  function formatMoney(cents) {
    var dollars = (cents / 100).toFixed(2);
    return dollars + ' $';
  }

  function track(eventName, params) {
    if (window.dataLayer) {
      window.dataLayer.push(Object.assign({ event: eventName }, params || {}));
    }
  }

  /* ---------- Bundle data ---------- */
  function readBundleData() {
    var node = $('[data-spdp-bundle-data]');
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (e) { console.error('[Sentinel bundle] bad JSON', e); return null; }
  }

  function getActiveVariantId() {
    /* Priority: URL ?variant > hidden form input > first key in by_variant_id */
    var url;
    try {
      url = new URL(window.location.href);
      var fromUrl = url.searchParams.get('variant');
      if (fromUrl) return fromUrl;
    } catch (e) {}
    var input = $('[data-spdp-variant-id]');
    if (input && input.value) return input.value;
    return null;
  }

  function getVariantData(bundleData, variantId) {
    if (!bundleData || !bundleData.by_variant_id) return null;
    if (variantId && bundleData.by_variant_id[variantId]) return bundleData.by_variant_id[variantId];
    var firstKey = Object.keys(bundleData.by_variant_id)[0];
    return firstKey ? bundleData.by_variant_id[firstKey] : null;
  }

  /* ---------- Shopify cart helpers ---------- */
  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  function addItemsToCart(items) {
    console.log('[Sentinel bundle] /cart/add.js payload:', items);
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      /* Always parse the body so we can surface Shopify's error message
         (e.g. which variant ID is unavailable or out of stock). */
      return r.json().then(function (body) {
        if (!r.ok) {
          console.error('[Sentinel bundle] cart-add ' + r.status, body);
          var msg = body && (body.description || body.message) || 'HTTP ' + r.status;
          var err = new Error(msg);
          err.shopifyBody = body;
          throw err;
        }
        return body;
      });
    });
  }

  function applyDiscount(code) {
    if (!code) return Promise.resolve();
    return fetch('/discount/' + encodeURIComponent(code), {
      method: 'GET',
      credentials: 'same-origin',
      redirect: 'manual'
    }).catch(function () { /* opaque redirect is expected */ });
  }

  function refreshCart() {
    /* Best-effort broadcast for anything in the theme that listens to these
       events. The cart drawer DOM is repainted explicitly via openCartDrawer
       below — events alone don't trigger a re-render in this theme. */
    try { document.dispatchEvent(new CustomEvent('cart:updated')); } catch (e) {}
    try { document.dispatchEvent(new CustomEvent('cart:refresh')); } catch (e) {}
    try { window.dispatchEvent(new Event('cart:refresh')); } catch (e) {}
    return fetch('/cart.js', { credentials: 'same-origin' }).catch(function () { return null; });
  }

  function openCartDrawer() {
    /* Canonical FGE pattern (moulee-pdp / fg-fastbuy): fetch the current
       page with ?sections=cart-drawer,cart-icon-bubble to get freshly
       server-rendered drawer HTML, then call cartDrawer.renderContents()
       which swaps the DOM and opens the drawer in one step. Clicking the
       header bubble alone only opens stale HTML and won't show new items. */
    var cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.renderContents !== 'function') {
      /* Fallback: synthesize a click on the cart bubble. The drawer will
         open with whatever was last rendered into it. */
      var cartLink = document.getElementById('cart-icon-bubble');
      if (cartLink) cartLink.click();
      return Promise.resolve();
    }
    if (cartDrawer.classList.contains('is-empty')) {
      cartDrawer.classList.remove('is-empty');
    }
    return fetch(window.location.pathname + '?sections=cart-drawer,cart-icon-bubble', {
      credentials: 'same-origin'
    })
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        cartDrawer.renderContents({ sections: sections });
      })
      .catch(function (err) {
        console.error('[Sentinel bundle] drawer render failed', err);
        var cartLink = document.getElementById('cart-icon-bubble');
        if (cartLink) cartLink.click();
      });
  }

  /* ---------- PDP bundle module ---------- */
  function initPDPBundle() {
    var bundle = $('[data-spdp-bundle]');
    var addBtn = $('[data-spdp-bundle-add]');
    var componentsList = $('[data-spdp-components]', bundle);
    if (!bundle || !addBtn || !componentsList) return;

    var bundleData = readBundleData();
    if (!bundleData) return;

    /* Per-render local state */
    var removed = Object.create(null);
    var projectLength = null; /* Set by calculator init or defaults to variant.length_ft */

    /* ---- Calculator helpers ---- */
    function getRollLength() {
      var calc = $('[data-spdp-calculator]', bundle);
      return calc ? (parseInt(calc.dataset.spdpRollLength, 10) || 100) : 100;
    }
    function getMaxLength() {
      var calc = $('[data-spdp-calculator]', bundle);
      return calc ? (parseInt(calc.dataset.spdpMaxLength, 10) || 1000) : 1000;
    }

    /* For the active project length, compute scaled quantities by role. */
    function computeProjectQty(linearFt) {
      var rollLength = getRollLength();
      return {
        rolls: Math.max(1, Math.ceil(linearFt / rollLength)),
        tpost: Math.ceil(linearFt / 8) + 2,
        tiewire: Math.max(1, Math.ceil(linearFt / 100)),
        driver: 1
      };
    }

    /* Per-component effective qty: role-based formulas above when known,
       otherwise fall back to the component's recipe quantity. */
    function effectiveQty(component, projectQty) {
      if (component.role === 'tpost') return projectQty.tpost;
      if (component.role === 'tiewire') return projectQty.tiewire;
      if (component.role === 'driver') return projectQty.driver;
      return component.quantity || 1;
    }

    function buildComponentHTML(comp) {
      var thumbImg = comp.image
        ? '<img src="' + escapeHtml(comp.image) + '" loading="lazy" class="spdp-bundle__component-thumb" alt="' + escapeHtml(comp.label) + '">'
        : '';
      var meta;
      var totalLabel;
      if (comp.variant_id && comp.available) {
        meta = formatMoney(comp.price_cents) + ' × ' + comp.quantity + (comp.required ? '' : ' · optionnel');
        totalLabel = formatMoney(comp.price_cents * comp.quantity);
      } else {
        meta = 'Produit à confirmer (handle: <code>' + escapeHtml(comp.handle) + '</code>)';
        totalLabel = 'n/d';
      }
      var unavailable = !(comp.variant_id && comp.available);
      var optional = !comp.required;

      var classes = ['spdp-bundle__component'];
      if (optional) classes.push('spdp-bundle__component--optional');
      if (unavailable) classes.push('spdp-bundle__component--unavailable');

      return (
        '<li class="' + classes.join(' ') + '"'
          + (unavailable ? '' : ' data-spdp-component'
            + ' data-spdp-component-role="' + escapeHtml(comp.role) + '"'
            + ' data-spdp-component-handle="' + escapeHtml(comp.handle) + '"'
            + ' data-spdp-component-variant-id="' + escapeHtml(comp.variant_id) + '"'
            + ' data-spdp-component-qty="' + escapeHtml(comp.quantity) + '"'
            + ' data-spdp-component-required="' + (comp.required ? 'true' : 'false') + '"'
            + ' data-spdp-component-price="' + escapeHtml(comp.price_cents) + '"'
          )
          + '>'
          + '<div class="spdp-bundle__component-img">' + thumbImg + '</div>'
          + '<div class="spdp-bundle__component-info">'
          +   '<span class="spdp-bundle__component-name">' + escapeHtml(comp.label) + '</span>'
          +   '<span class="spdp-bundle__component-meta">' + meta + '</span>'
          + '</div>'
          + '<div class="spdp-bundle__component-total">' + totalLabel + '</div>'
          + (unavailable ? '' : '<button type="button" class="spdp-bundle__component-remove" data-spdp-component-remove aria-label="Retirer du kit"><svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg></button>')
        + '</li>'
      );
    }

    /* Tween a numeric (cents) value displayed via formatMoney over ~280ms. */
    function tweenMoney(el, fromCents, toCents) {
      if (!el || fromCents === toCents) {
        if (el) el.textContent = formatMoney(toCents);
        return;
      }
      var start = performance.now();
      var duration = 280;
      function tick(now) {
        var t = Math.min(1, (now - start) / duration);
        var eased = 1 - Math.pow(1 - t, 3); /* easeOutCubic */
        var current = Math.round(fromCents + (toCents - fromCents) * eased);
        el.textContent = formatMoney(current);
        if (t < 1) requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
    }

    function calculateAndUpdateTotals(variantData) {
      var linearFt = projectLength || (variantData.length_ft || getRollLength());
      var projectQty = computeProjectQty(linearFt);
      var fencePriceCents = variantData.price_cents || 0;
      var fenceSubtotal = fencePriceCents * projectQty.rolls;

      var componentsSubtotal = 0;
      variantData.components.forEach(function (c) {
        if (removed[c.handle]) return;
        if (!c.variant_id || !c.available) return;
        componentsSubtotal += c.price_cents * effectiveQty(c, projectQty);
      });

      /* Subtotal shown in the bundle: full project (rolls + components). */
      var subtotal = fenceSubtotal + componentsSubtotal;

      var allRequiredPresent = variantData.components.every(function (c) {
        if (!c.required) return true;
        if (removed[c.handle]) return false;
        return c.variant_id && c.available;
      });

      /* Discount applies to KIT components only (per brief 04). */
      var discountPct = allRequiredPresent ? variantData.discount_percent : 0;
      var discountAmount = Math.floor(componentsSubtotal * discountPct / 100);
      var finalTotal = subtotal - discountAmount;

      var subRaw = $('[data-spdp-subtotal-raw]', bundle);
      var subFinal = $('[data-spdp-subtotal-final]', bundle);
      var savings = $('[data-spdp-savings]', bundle);
      var discountPctEl = $('[data-spdp-discount-pct]', bundle);
      var ctaDiscount = $('[data-spdp-cta-discount]', bundle);

      /* Parse the previous displayed value so the tween starts from the
         visible number rather than snapping. */
      function readDisplayedCents(el) {
        if (!el) return 0;
        var txt = el.textContent.replace(/[^\d.,-]/g, '').replace(',', '.');
        var n = parseFloat(txt) || 0;
        return Math.round(n * 100);
      }

      if (subRaw) tweenMoney(subRaw, readDisplayedCents(subRaw), subtotal);
      if (subFinal) tweenMoney(subFinal, readDisplayedCents(subFinal), finalTotal);
      if (savings) tweenMoney(savings, readDisplayedCents(savings), discountAmount);
      if (discountPctEl) discountPctEl.textContent = discountPct;
      if (ctaDiscount) ctaDiscount.textContent = discountPct;

      /* Update per-component row meta + line total for the current qty. */
      $$('[data-spdp-component]', componentsList).forEach(function (row) {
        var role = row.dataset.spdpComponentRole;
        var basePrice = parseInt(row.dataset.spdpComponentPrice, 10) || 0;
        var component = variantData.components.find(function (c) { return c.role === role; });
        if (!component) return;
        var qty = effectiveQty(component, projectQty);
        row.dataset.spdpComponentQty = qty;
        var metaEl = row.querySelector('.spdp-bundle__component-meta');
        var totalEl = row.querySelector('.spdp-bundle__component-total');
        if (metaEl) {
          var optionalSuffix = component.required ? '' : ' · optionnel';
          metaEl.innerHTML = formatMoney(basePrice) + ' × ' + qty + optionalSuffix;
        }
        if (totalEl) totalEl.textContent = formatMoney(basePrice * qty);
      });

      /* Update the calculator help line + coverage span. */
      var rollsCountEl = $('[data-spdp-rolls-count]', bundle);
      var tpostCountEl = $('[data-spdp-tpost-count]', bundle);
      var tiewireCountEl = $('[data-spdp-tiewire-count]', bundle);
      var coverageFtEl = $('[data-spdp-bundle-coverage-ft]', bundle);
      if (rollsCountEl) rollsCountEl.textContent = projectQty.rolls;
      if (tpostCountEl) tpostCountEl.textContent = projectQty.tpost;
      if (tiewireCountEl) tiewireCountEl.textContent = projectQty.tiewire;
      if (coverageFtEl) coverageFtEl.textContent = linearFt;

      /* Free-delivery progress bar */
      var progressWrap = $('[data-spdp-delivery-progress]', bundle);
      var progressFill = $('[data-spdp-delivery-progress-fill]', bundle);
      var progressText = $('[data-spdp-delivery-progress-text]', bundle);
      if (progressWrap && progressFill && progressText) {
        var pct = Math.min(100, Math.round((finalTotal / FREE_DELIVERY_THRESHOLD_CENTS) * 100));
        progressFill.style.width = pct + '%';
        var qualified = finalTotal >= FREE_DELIVERY_THRESHOLD_CENTS;
        progressWrap.classList.toggle('spdp-bundle__delivery-progress--qualified', qualified);
        if (qualified) {
          progressText.innerHTML = '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true" style="vertical-align:-2px"><path d="M3 8l3 3 7-7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg> Livraison locale gratuite incluse avec ce kit<sup>*</sup>';
        } else {
          var remaining = FREE_DELIVERY_THRESHOLD_CENTS - finalTotal;
          progressText.innerHTML = 'Encore <strong>' + formatMoney(remaining) + '</strong> pour la livraison locale gratuite<sup>*</sup> (95$+)';
        }
      }

      if (allRequiredPresent && discountPct > 0) {
        addBtn.textContent = 'Ajouter le projet complet au panier · économisez ' + discountPct + '%';
      } else {
        addBtn.textContent = 'Ajouter les articles sélectionnés';
      }

      return { subtotal: subtotal, discountPct: discountPct, discountCode: allRequiredPresent ? variantData.discount_code : null };
    }

    function wireRemoveButtons(variantData) {
      $$('[data-spdp-component-remove]', componentsList).forEach(function (btn) {
        btn.addEventListener('click', function () {
          var row = btn.closest('[data-spdp-component]');
          if (!row) return;
          var handle = row.dataset.spdpComponentHandle;
          removed[handle] = true;
          row.classList.add('spdp-bundle__component--removing');
          btn.style.display = 'none';
          track('bundle_component_removed', {
            parent_fence_sku: variantData.sku,
            component_handle: handle,
            required: row.dataset.spdpComponentRequired === 'true'
          });
          calculateAndUpdateTotals(variantData);
        });
      });
    }

    function renderVariant(variantId) {
      var variantData = getVariantData(bundleData, variantId);
      if (!variantData) return;

      removed = Object.create(null); /* fresh state for new variant */

      componentsList.innerHTML = variantData.components.map(buildComponentHTML).join('');
      wireRemoveButtons(variantData);
      calculateAndUpdateTotals(variantData);
    }

    /* Add the project click handler */
    addBtn.addEventListener('click', function () {
      if (addBtn.disabled) return;
      var activeVariantId = getActiveVariantId();
      var variantData = getVariantData(bundleData, activeVariantId);
      if (!variantData) return;

      var originalLabel = addBtn.textContent;
      addBtn.disabled = true;
      addBtn.textContent = 'Ajout en cours...';

      /* Use the calculator's project length to scale quantities. */
      var linearFt = projectLength || (variantData.length_ft || getRollLength());
      var projectQty = computeProjectQty(linearFt);

      var componentItems = variantData.components
        .filter(function (c) {
          if (removed[c.handle]) return false;
          if (!c.variant_id || !c.available) return false;
          return true;
        })
        .map(function (c) {
          return {
            id: c.variant_id,
            quantity: effectiveQty(c, projectQty),
            properties: { '_bundle_id': variantData.sku, '_bundle_role': 'kit_component' }
          };
        });

      if (componentItems.length === 0) {
        addBtn.disabled = false;
        addBtn.textContent = 'Aucun composant disponible';
        return;
      }

      var totals = calculateAndUpdateTotals(variantData);
      var discountCode = totals.discountCode;
      var fenceVariantId = parseInt(activeVariantId, 10);

      /* Check the cart for the parent fence. Add the delta needed to reach
         projectQty.rolls — so a 200ft project on a 100ft variant adds 2
         rolls (or 1 more if the customer already has 1 in cart). */
      getCart()
        .then(function (cart) {
          var fenceInCartQty = 0;
          (cart.items || []).forEach(function (i) {
            if (i.variant_id === fenceVariantId) fenceInCartQty += i.quantity;
          });
          var fenceDelta = Math.max(0, projectQty.rolls - fenceInCartQty);
          var items = componentItems;
          if (fenceDelta > 0 && fenceVariantId) {
            items = [{
              id: fenceVariantId,
              quantity: fenceDelta,
              properties: { '_bundle_id': variantData.sku, '_bundle_role': 'kit_parent' }
            }].concat(componentItems);
          }
          return addItemsToCart(items);
        })
        .then(function () { return applyDiscount(discountCode); })
        .then(function () {
          var discountAmount = Math.floor(totals.subtotal * totals.discountPct / 100);
          track('bundle_kit_added', {
            parent_fence_sku: variantData.sku,
            components_count: componentItems.length,
            subtotal_before_discount: totals.subtotal,
            discount_amount: discountAmount,
            discount_code: discountCode || null
          });

          return refreshCart().then(function () {
            openCartDrawer();
            addBtn.textContent = 'Projet ajouté ✓';
            setTimeout(function () {
              addBtn.disabled = false;
              addBtn.textContent = originalLabel;
            }, 3000);
          });
        })
        .catch(function (err) {
          console.error('[Sentinel bundle]', err);
          addBtn.disabled = false;
          addBtn.textContent = 'Erreur, réessayez';
          setTimeout(function () { addBtn.textContent = originalLabel; }, 3000);
        });
    });

    /* GA4 viewport event */
    if ('IntersectionObserver' in window) {
      var fired = false;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting && !fired) {
            fired = true;
            var variantData = getVariantData(bundleData, getActiveVariantId());
            track('bundle_module_viewed', {
              parent_fence_sku: variantData ? variantData.sku : null
            });
            io.disconnect();
          }
        });
      }, { threshold: 0.4 });
      io.observe(bundle);
    }

    /* Project calculator wireup */
    function initCalculator() {
      var calc = $('[data-spdp-calculator]', bundle);
      if (!calc) return;
      var input = $('[data-spdp-project-length]', calc);
      var minusBtn = $('[data-spdp-length-minus]', calc);
      var plusBtn = $('[data-spdp-length-plus]', calc);
      var overflowEl = $('[data-spdp-calculator-overflow]', calc);
      var helpEl = $('[data-spdp-calculator-help]', calc);
      if (!input) return;
      var rollLength = getRollLength();
      var maxLength = getMaxLength();

      function clamp(val) {
        if (isNaN(val) || val < rollLength) return rollLength;
        return val;
      }

      function applyLength(rawVal) {
        var val = parseInt(rawVal, 10);
        var over = val > maxLength;
        val = clamp(val);
        projectLength = over ? maxLength : val;
        input.value = projectLength;
        /* Disable plus when at or above max */
        if (plusBtn) plusBtn.disabled = projectLength >= maxLength;
        if (minusBtn) minusBtn.disabled = projectLength <= rollLength;
        /* Overflow message + disable add */
        if (overflowEl) overflowEl.hidden = !over;
        if (helpEl) helpEl.hidden = over;
        if (addBtn) addBtn.disabled = over;

        var variantData = getVariantData(bundleData, getActiveVariantId());
        if (variantData) calculateAndUpdateTotals(variantData);
      }

      if (minusBtn) minusBtn.addEventListener('click', function () {
        applyLength((parseInt(input.value, 10) || rollLength) - rollLength);
      });
      if (plusBtn) plusBtn.addEventListener('click', function () {
        applyLength((parseInt(input.value, 10) || rollLength) + rollLength);
      });
      input.addEventListener('change', function () { applyLength(input.value); });
      /* On `input` events we want responsive feedback but not jittery
         clamping until the user is done. Use a small debounce. */
      var inputDebounce;
      input.addEventListener('input', function () {
        clearTimeout(inputDebounce);
        inputDebounce = setTimeout(function () { applyLength(input.value); }, 250);
      });

      /* Initialize state */
      projectLength = clamp(parseInt(input.value, 10) || rollLength);
      input.value = projectLength;
    }

    /* React to variant changes from sentinel-pdp.js */
    document.addEventListener('variant:change', function (e) {
      var newVariantId = e.detail && e.detail.variant_id;
      if (!newVariantId) return;
      renderVariant(newVariantId);

      /* Header coverage line: format + project length */
      var formatSpan = $('[data-spdp-bundle-format]', bundle);
      if (formatSpan && e.detail.height_ft && e.detail.length_ft) {
        formatSpan.textContent = e.detail.height_ft + "'x" + e.detail.length_ft + "'";
      }

      /* Reset the calculator input + min step to the new variant's roll length */
      var calc = $('[data-spdp-calculator]', bundle);
      if (calc && e.detail.length_ft) {
        var newRollLength = parseInt(e.detail.length_ft, 10);
        calc.dataset.spdpRollLength = newRollLength;
        var input = $('[data-spdp-project-length]', calc);
        if (input) {
          input.min = newRollLength;
          input.step = newRollLength;
          input.value = newRollLength;
        }
        projectLength = newRollLength;
        /* Recompute with new variant data */
        var newVariantData = getVariantData(bundleData, newVariantId);
        if (newVariantData) calculateAndUpdateTotals(newVariantData);
      }
    });

    /* Initial wire-up on the server-rendered DOM (don't re-render — it's
       already in the DOM correctly). Just attach handlers. */
    var initialVariant = getVariantData(bundleData, getActiveVariantId());
    if (initialVariant) {
      wireRemoveButtons(initialVariant);
      initCalculator();
      /* Recompute totals using the cleaner JS path. The server-rendered
         numbers should match but this keeps the data path consistent. */
      calculateAndUpdateTotals(initialVariant);
    }
  }

  /* ---------- Cart drawer suggestion ---------- */
  function isDismissed() {
    try { return sessionStorage.getItem(SESSION_DISMISSED_KEY) === '1'; } catch (e) { return false; }
  }

  function setDismissed() {
    try { sessionStorage.setItem(SESSION_DISMISSED_KEY, '1'); } catch (e) {}
  }

  function findFenceVariantInCart(cart, bundleData) {
    if (!bundleData || !bundleData.by_variant_id) return null;
    return (cart.items || []).find(function (item) {
      return bundleData.by_variant_id[String(item.variant_id)];
    });
  }

  function hasKitInCart(cart) {
    return (cart.items || []).some(function (i) {
      return i.properties && i.properties._bundle_role === 'kit_component';
    });
  }

  function findDrawerInjectionTarget() {
    return (
      document.querySelector('cart-drawer .drawer__cart-items-wrapper') ||
      document.querySelector('cart-drawer .drawer__inner') ||
      null
    );
  }

  /* ---------- Visible kit-discount banner ----------
     The kit codes (SENTINEL_KIT_10/12/15) are session-captured via
     /discount/<code> after the kit is added, but Shopify doesn't always
     reflect the discount in cart.js until checkout. To make the discount
     visible/trustworthy to the customer BEFORE they reach checkout, we
     inject a confirmation banner in the cart drawer showing the code + %
     whenever kit components are detected in the cart.
     Derives code + percent from bundle data + line-item properties,
     not from cart.discount_applications (which may be empty pre-checkout). */
  var KIT_DISCOUNT_BANNER_ID = 'sentinel-kit-discount-banner';

  function findKitDiscountInfo(cart, bundleData) {
    if (!bundleData || !bundleData.by_variant_id) return null;
    var kitItem = (cart.items || []).find(function (i) {
      return i.properties && i.properties._bundle_role === 'kit_component';
    });
    if (!kitItem) return null;
    var bundleSku = kitItem.properties && kitItem.properties._bundle_id;
    if (!bundleSku) return null;
    var keys = Object.keys(bundleData.by_variant_id);
    for (var i = 0; i < keys.length; i++) {
      var vd = bundleData.by_variant_id[keys[i]];
      if (vd.sku === bundleSku && vd.discount_code && vd.discount_percent > 0) {
        return {
          code: vd.discount_code,
          percent: vd.discount_percent,
          variantSku: vd.sku
        };
      }
    }
    return null;
  }

  function injectKitDiscountBanner(cart, bundleData) {
    var existing = document.getElementById(KIT_DISCOUNT_BANNER_ID);
    var info = findKitDiscountInfo(cart, bundleData);

    if (!info) {
      if (existing) existing.remove();
      return;
    }

    var target = findDrawerInjectionTarget();
    if (!target) return;

    if (existing) existing.remove(); /* re-render to refresh % if variant changed */

    var banner = document.createElement('div');
    banner.id = KIT_DISCOUNT_BANNER_ID;
    banner.className = 'spdp-kit-discount-banner';
    banner.innerHTML = [
      '<svg class="spdp-kit-discount-banner__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">',
      '  <path d="M3 12l3.5 3.5L14 8M21 12l-7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
      '</svg>',
      '<div class="spdp-kit-discount-banner__body">',
      '  <p class="spdp-kit-discount-banner__title">Code <code>' + escapeHtml(info.code) + '</code> appliqué</p>',
      '  <p class="spdp-kit-discount-banner__sub">Rabais de ' + info.percent + ' % sur les composants du kit à la caisse.</p>',
      '</div>'
    ].join('');
    target.parentNode.insertBefore(banner, target);
  }

  /* Hook into cart-drawer lifecycle: re-run after the drawer renders or
     when cart contents change. Idempotent — injectKitDiscountBanner
     removes any existing banner before deciding whether to re-add. */
  function refreshKitDiscountBanner() {
    var bundleData = readBundleData();
    if (!bundleData) return;
    getCart().then(function (cart) {
      injectKitDiscountBanner(cart, bundleData);
    }).catch(function () {});
  }

  /* Compute the kit's net cost (subtotal × (1 − discount_percent / 100)). */
  function computeKitNetCents(variantData) {
    var subtotal = variantData.components.reduce(function (sum, c) {
      if (!c.variant_id || !c.available) return sum;
      return sum + (c.price_cents * c.quantity);
    }, 0);
    var discount = Math.floor(subtotal * (variantData.discount_percent || 0) / 100);
    return subtotal - discount;
  }

  function buildSuggestionHTML(variantData, cart) {
    /* ----- Pricing breakdown (Tier 1: dollar anchor + price-baked CTA) ----- */
    var subtotal = variantData.components.reduce(function (sum, c) {
      if (!c.variant_id || !c.available) return sum;
      return sum + (c.price_cents * c.quantity);
    }, 0);
    var discountPct = variantData.discount_percent || 0;
    var savings = Math.floor(subtotal * discountPct / 100);
    var netTotal = subtotal - savings;

    /* ----- Components with thumbnails (Tier 1) ----- */
    var componentLis = variantData.components
      .filter(function (c) { return c.variant_id && c.available; })
      .map(function (c) {
        var thumb = c.image
          ? '<img class="spdp-cart-suggest__thumb" src="' + escapeHtml(c.image) + '" alt="" loading="lazy" width="32" height="32">'
          : '<span class="spdp-cart-suggest__thumb spdp-cart-suggest__thumb--blank" aria-hidden="true"></span>';
        return [
          '<li class="spdp-cart-suggest__component">',
          '  ' + thumb,
          '  <span class="spdp-cart-suggest__component-label">' + escapeHtml(c.label) + '</span>',
          '  <span class="spdp-cart-suggest__component-qty">× ' + c.quantity + '</span>',
          '</li>'
        ].join('');
      })
      .join('');

    /* ----- Free-delivery progress bar — Tier 2: hide when gap large ----- */
    var currentCartCents = (cart && cart.total_price) ? cart.total_price : 0;
    var projectedTotal = currentCartCents + netTotal;
    var remaining = FREE_DELIVERY_THRESHOLD_CENTS - projectedTotal;
    var qualified = projectedTotal >= FREE_DELIVERY_THRESHOLD_CENTS;
    var almostQualified = !qualified && remaining > 0 && remaining < 500; /* < 5 $ */
    var pct = Math.min(100, Math.round((projectedTotal / FREE_DELIVERY_THRESHOLD_CENTS) * 100));

    var progressBlock = '';
    if (qualified || almostQualified) {
      var progressText = qualified
        ? '<svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true" style="vertical-align:-1px"><path d="M3 8l3 3 7-7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg> Avec ce kit, livraison locale gratuite incluse'
        : 'Presque! Encore <strong>' + formatMoney(remaining) + '</strong> pour la livraison gratuite';
      progressBlock = [
        '<div class="spdp-cart-suggest__progress' + (qualified ? ' spdp-cart-suggest__progress--qualified' : '') + '">',
        '  <div class="spdp-cart-suggest__progress-track">',
        '    <div class="spdp-cart-suggest__progress-fill" style="width:' + pct + '%"></div>',
        '  </div>',
        '  <p class="spdp-cart-suggest__progress-text">',
        '    ' + progressText,
        '    <span class="spdp-cart-suggest__tooltip-wrap">',
        '      <button type="button" class="spdp-cart-suggest__tooltip-trigger" aria-label="Détails sur la livraison gratuite">',
        '        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.3"/><path d="M8 7v4M8 5h.01" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>',
        '      </button>',
        '      <span class="spdp-cart-suggest__tooltip" role="tooltip">Adresse admissible dans notre zone (40 km de West Brome). Vérifié à la caisse.</span>',
        '    </span>',
        '  </p>',
        '</div>'
      ].join('\n');
    }

    /* ----- Title + format subline (Tier 1: benefit + project-fit signal) ----- */
    var formatLabel = variantData.format ? escapeHtml(variantData.format) : '';
    var titleBlock = [
      '<p class="spdp-cart-suggest__title">L\'équipement complet pour installer votre clôture</p>',
      formatLabel
        ? '<p class="spdp-cart-suggest__subline">Calculé pour votre format <strong>' + formatLabel + '</strong></p>'
        : ''
    ].join('');

    /* ----- Price summary (Tier 1: dollar anchor with strike-through) ----- */
    var priceSummary;
    if (discountPct > 0 && savings > 0) {
      priceSummary = [
        '<div class="spdp-cart-suggest__price-summary">',
        '  <span class="spdp-cart-suggest__price-label">Total du kit</span>',
        '  <span class="spdp-cart-suggest__price-values">',
        '    <s class="spdp-cart-suggest__price-strike">' + formatMoney(subtotal) + '</s>',
        '    <strong class="spdp-cart-suggest__price-net">' + formatMoney(netTotal) + '</strong>',
        '    <span class="spdp-cart-suggest__price-savings">économisez ' + formatMoney(savings) + '</span>',
        '  </span>',
        '</div>'
      ].join('\n');
    } else {
      priceSummary = [
        '<div class="spdp-cart-suggest__price-summary">',
        '  <span class="spdp-cart-suggest__price-label">Total du kit</span>',
        '  <strong class="spdp-cart-suggest__price-net">' + formatMoney(netTotal) + '</strong>',
        '</div>'
      ].join('\n');
    }

    return [
      '<div class="spdp-cart-suggest__header">',
      '  <span class="spdp-cart-suggest__eyebrow">COMPLÉTEZ VOTRE PROJET</span>',
      '  <button type="button" class="spdp-cart-suggest__dismiss" aria-label="Fermer la suggestion" data-spdp-dismiss>',
      '    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>',
      '  </button>',
      '</div>',
      titleBlock,
      '<ul class="spdp-cart-suggest__list">' + componentLis + '</ul>',
      progressBlock,
      priceSummary,
      '<button type="button" class="spdp-btn spdp-btn--primary spdp-cart-suggest__add" data-spdp-cart-add>',
      '  Compléter mon projet · <strong>' + formatMoney(netTotal) + '</strong>',
      '</button>',
      '<p class="spdp-cart-suggest__reassurance">Pas satisfait? Retour 30 jours, rouleau non-déroulé.</p>',
      '<button type="button" class="spdp-cart-suggest__decline" data-spdp-decline>Non merci</button>'
    ].join('\n');
  }

  function injectSuggestion(bundleData, variantData, cart) {
    if (isDismissed()) return;
    if (document.getElementById(INJECTED_ID)) return;
    var target = findDrawerInjectionTarget();
    if (!target) return;

    var wrap = document.createElement('div');
    wrap.id = INJECTED_ID;
    wrap.className = 'spdp-cart-suggest';
    wrap.setAttribute('data-spdp-cart-suggest', '');
    wrap.innerHTML = buildSuggestionHTML(variantData, cart);
    target.parentNode.insertBefore(wrap, target);

    function dismiss(reason) {
      setDismissed();
      wrap.remove();
      track('cart_drawer_bundle_dismissed', {
        parent_fence_sku: variantData.sku,
        reason: reason || 'dismiss'
      });
    }
    wrap.querySelector('[data-spdp-dismiss]').addEventListener('click', function () { dismiss('close'); });
    wrap.querySelector('[data-spdp-decline]').addEventListener('click', function () { dismiss('decline'); });

    wrap.querySelector('[data-spdp-cart-add]').addEventListener('click', function () {
      var btn = this;
      btn.disabled = true;
      btn.textContent = 'Ajout en cours...';

      var items = variantData.components
        .filter(function (c) { return c.variant_id && c.available; })
        .map(function (c) {
          return {
            id: c.variant_id,
            quantity: c.quantity,
            properties: { '_bundle_id': variantData.sku, '_bundle_role': 'kit_component' }
          };
        });

      addItemsToCart(items)
        .then(function () { return applyDiscount(variantData.discount_code); })
        .then(function () {
          track('cart_drawer_bundle_added', {
            parent_fence_sku: variantData.sku,
            components_count: items.length
          });
          wrap.remove();
          /* Suggestion lives INSIDE the drawer — repaint via renderContents
             so the new line items appear and the suggestion's own DOM
             (already removed above) doesn't ghost-render. */
          return refreshCart().then(openCartDrawer);
        })
        .catch(function (err) {
          console.error('[Sentinel bundle] cart-drawer add failed', err);
          btn.disabled = false;
          btn.textContent = 'Erreur, réessayez';
        });
    });
  }

  function checkAndShowSuggestion() {
    if (isDismissed()) return;
    var bundleData = readBundleData();
    if (!bundleData) return;

    getCart().then(function (cart) {
      var fenceItem = findFenceVariantInCart(cart, bundleData);
      if (!fenceItem) return;
      if (hasKitInCart(cart)) return;
      var variantData = bundleData.by_variant_id[String(fenceItem.variant_id)];
      if (!variantData) return;
      injectSuggestion(bundleData, variantData, cart);
    }).catch(function () {});
  }

  function initCartDrawerWatcher() {
    var onDrawerChange = function () {
      checkAndShowSuggestion();
      refreshKitDiscountBanner();
    };
    document.addEventListener('cart:open', onDrawerChange);
    document.addEventListener('cart:update', onDrawerChange);
    document.addEventListener('cart:refresh', onDrawerChange);
    document.addEventListener('cart:opened', onDrawerChange);

    var drawer = document.querySelector('cart-drawer');
    if (drawer) {
      var observer = new MutationObserver(function () {
        if (drawer.classList.contains('active') || drawer.classList.contains('is-open')) {
          onDrawerChange();
        }
      });
      observer.observe(drawer, { attributes: true, attributeFilter: ['class', 'open'] });
    }
  }

  /* ---------- Boot ---------- */
  function init() {
    initPDPBundle();
    initCartDrawerWatcher();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
